import React, { useState, useContext, memo, useCallback, useMemo } from "react";

import { BodyContainer } from "App/styles";

import { MainTitle } from "components/MainTitle";

import useCrudFlow from "hooks/useCrudFlow";

import ListOf from "components/ListOf";
import Modal from "components/Modal";
import Confirmation from "components/Confirmation";

import ToolBar from "components/ToolBar";

import ItemForm from "components/ItemForm";

import { BackToLinkButton } from "basics/buttons";

import { SubTitle } from "./styles";

import { AlertContext } from "contexts/AlertContext";
import { MessageType } from "model/dictionary";

const AdminPage = memo(
  ({
    title,
    subTitle,
    seoTitle,
    collection,
    schema,
    sortOptions,
    externalCreateItem,
    externalUpdateItem,
    externalDeleteItem,
    ItemFormFields,
    ItemCard,
    ItemToolBar,
    backTo,
    orderBy,
    realDeletion = false,
  }) => {
    const {
      loading,
      items,
      updateFiltering,
      updateSorting,
      getItems,
      updateItem,
      createItem,
      setOnlyActived,
      deleteItem,
      switchActivationItem,
    } = useCrudFlow(collection, orderBy || "title");

    const [itemSelectedId, setItemSelectedId] = useState();
    const [itemDeletedId, setItemDeletedId] = useState();
    const [openNewForm, setOpenNewForm] = useState(false);
    const { showAlert } = useContext(AlertContext);

    const [waiting, setWaiting] = useState(false);

    // Item siendo editado / siendo borrado (resueltos por id contra el array de items).
    const editingItem = useMemo(
      () => items.find((it) => it.id === itemSelectedId),
      [items, itemSelectedId]
    );
    const deletingItem = useMemo(
      () => items.find((it) => it.id === itemDeletedId),
      [items, itemDeletedId]
    );

    const onOpenNewForm = useCallback(() => {
      setItemSelectedId();
      setOpenNewForm(true);
    }, []);

    const onSelectEditedItem = useCallback(
      (id) => {
        setItemSelectedId(id === itemSelectedId ? null : id);
      },
      [itemSelectedId]
    );

    const onSelectDeletedItem = useCallback(
      (id) => {
        setItemDeletedId(id === itemDeletedId ? null : id);
      },
      [itemDeletedId]
    );

    // Cierra cualquier modal abierto (creación, edición, eliminación).
    const onCleanItemId = useCallback(() => {
      setItemDeletedId();
      setItemSelectedId();
      setOpenNewForm(false);
    }, []);

    const executePromise = useCallback(
      (promise) => {
        setWaiting(true);

        promise
          .then(() => {
            onCleanItemId();

            // El listado se recarga a mano. Antes lo hacía de rebote un
            // `reset()` que blanqueaba filtros y orden, así que borrar un ítem
            // filtrado devolvía la lista entera mientras el toolbar seguía
            // mostrando el filtro tipeado (los campos son no controlados).
            getItems();

            showAlert(
              MessageType.SUCESSFULL,
              "Actualización satisfactoria",
              2000
            );
          })
          .catch((error) => {
            showAlert(MessageType.DANGER, `Se produjo un error ${error}`, 2000);
          })
          .finally(() => {
            setWaiting(false);
          });
      },
      [getItems, onCleanItemId, showAlert]
    );

    const onDeleteItem = useCallback(
      (id) => {
        var promise = externalDeleteItem
          ? externalDeleteItem(id)
          : realDeletion
          ? deleteItem(id)
          : switchActivationItem(id, false);

        executePromise(promise);
      },
      [
        deleteItem,
        executePromise,
        externalDeleteItem,
        realDeletion,
        switchActivationItem,
      ]
    );

    const onReactivateItem = useCallback(
      (id) => {
        executePromise(switchActivationItem(id, true));
      },
      [executePromise, switchActivationItem]
    );

    const onUpdateItem = useCallback(
      (id, fields) => {
        var promise = externalUpdateItem
          ? externalUpdateItem(id, fields)
          : updateItem(id, fields);

        executePromise(promise);
      },
      [executePromise, externalUpdateItem, updateItem]
    );

    const onCreateItem = useCallback(
      (fields) => {
        const promise = externalCreateItem
          ? externalCreateItem(fields)
          : createItem(fields);

        executePromise(promise);
      },
      [createItem, executePromise, externalCreateItem]
    );

    const itemFormFields = (
      register,
      unregister,
      setValue,
      errors,
      item,
      setPending
    ) => (
      <ItemFormFields
        register={register}
        unregister={unregister}
        setValue={setValue}
        errors={errors}
        item={item}
        setPending={setPending}
      />
    );

    const itemForm = (item) => (
      <ItemForm
        waiting={waiting}
        formFields={itemFormFields}
        schema={schema}
        item={item}
        onCreateItem={onCreateItem}
        onUpdateItem={onUpdateItem}
        onCancel={onCloseModal}
      />
    );

    const itemCard = (item, selected) => (
      <ItemCard
        onCleanItemId={onCleanItemId}
        onReactivateItem={onReactivateItem}
        onSelectEditedItem={onSelectEditedItem}
        onSelectDeletedItem={onSelectDeletedItem}
        item={item}
        selected={selected}
      />
    );

    const itemToolbar = () => <ItemToolBar updateFiltering={updateFiltering} />;

    // Cierre genérico de cualquier modal (ESC, click backdrop, o cancelar).
    // El form se desmonta con el modal, así que su estado interno se limpia
    // solo: no hay nada que resetear acá.
    const onCloseModal = onCleanItemId;

    return (
      <BodyContainer>
        <MainTitle title={title} seoTitle={seoTitle} />

        <ToolBar
          onOpenNewForm={onOpenNewForm}
          onSorting={updateSorting}
          sortOptions={sortOptions}
          showNewButton={true}
          onOnlyActivated={setOnlyActived}
        >
          {itemToolbar()}
        </ToolBar>

        {subTitle && <SubTitle>{subTitle}</SubTitle>}

        {backTo && (
          <BackToLinkButton to={backTo.url}>{backTo.title}</BackToLinkButton>
        )}

        <ListOf
          loading={loading}
          items={items}
          cardItem={itemCard}
          itemSelectedId={itemSelectedId}
          itemDeletedId={itemDeletedId}
        />

        {/* Los 3 modales van `locked` siempre: ni ESC ni click backdrop los cierran.
            La salida solo es por el botón explícito (Cancelar / Guardar / Aceptar). */}

        {/* Modal de creación — wide para matchear el ancho del ListOf */}
        <Modal open={openNewForm} onClose={onCloseModal} locked wide>
          {openNewForm && itemForm()}
        </Modal>

        {/* Modal de edición — wide para matchear el ancho del ListOf */}
        <Modal open={!!editingItem} onClose={onCloseModal} locked wide>
          {editingItem && itemForm(editingItem)}
        </Modal>

        {/* Modal de confirmación de eliminación — compacto */}
        <Modal open={!!deletingItem} onClose={onCloseModal} locked>
          {deletingItem && (
            <Confirmation
              type="cancel"
              paramOk={deletingItem.id}
              text={`Desea eliminar ${deletingItem.title}?`}
              onClick={onDeleteItem}
              cancel={onCleanItemId}
              paramCancel={false}
            />
          )}
        </Modal>
      </BodyContainer>
    );
  }
);

export default AdminPage;
